import { Entity } from '@backstage/catalog-model';
import type { JsonValue } from '@backstage/types';

/**
 * Call-to-action button configuration
 */
export interface CallToAction {
  /** Index signature for JsonObject compatibility */
  [key: string]: JsonValue | undefined;
  /** Button/link text */
  label?: string;
  /** Additional context for the action */
  note?: string;
  /** Target URL for the action */
  url?: string;
}

/**
 * Valid recommended policy states for tool adoption
 */
export type RecommendedState = 'adopt' | 'hold' | 'reject' | 'trial';

/**
 * Recommended policy for tool adoption
 */
export interface RecommendedPolicy {
  /** Index signature for JsonObject compatibility */
  [key: string]: JsonValue | undefined;
  /** Explanation for the recommendation */
  rationale?: string;
  /** Recommendation state (e.g., adopt, trial, hold, reject) */
  state?: RecommendedState;
}

/**
 * Spec definition for Tool entities
 */
export interface ToolEntitySpec {
  /** Index signature for JsonObject compatibility */
  [key: string]: JsonValue | undefined;
  /** Detailed description of the tool (markdown supported) */
  aboutText?: string;
  /** Call-to-action button configuration */
  callToAction?: CallToAction;
  /** Functional capabilities provided (free-form text) */
  capabilities: string[];
  /** Link to internal documentation (SharePoint, Confluence, etc.) */
  internalDocsUrl?: string;
  /** Marketing/display name for the tool */
  offeringName: string;
  /** Entity reference to the owning team (e.g., group:default/team-name) */
  owner?: string;
  /** Recommended policy for tool adoption */
  recommendedPolicy?: RecommendedPolicy;
  /** SDLC phases where this tool is used (free-form text) */
  sdlcPhases: string[];
  /** Support channel information */
  supportChannel?: SupportChannel;
  /** Technology stacks this tool supports (free-form text) */
  techStacks?: string[];
  /** URL or base64-encoded image for the tool icon */
  thumbnail?: string;
}

/**
 * Support channel information for contacting tool maintainers
 */
export interface SupportChannel {
  /** Index signature for JsonObject compatibility */
  [key: string]: JsonValue | undefined;
  /** Human-readable channel or contact name */
  channelName?: string;
  /** Platform type (e.g., slack, teams, email) */
  platform?: 'email' | 'slack' | 'teams';
  /** Direct link to support channel */
  url: string;
}

/**
 * API version for NS custom entities
 */
export const NS_API_VERSION = 'ns.nl/v1alpha1';

/**
 * Entity kind for tools
 */
export const TOOL_KIND = 'Tool';

/**
 * Tool entity for NS Tool Overview
 *
 * Represents a tool that teams can adopt and use.
 * Tools are categorized by SDLC phase, capabilities, and tech stack.
 */
export interface ToolEntity extends Entity {
  apiVersion: typeof NS_API_VERSION;
  kind: typeof TOOL_KIND;
  spec: ToolEntitySpec;
}

/**
 * Type guard to check if an entity is a Tool
 */
export function isToolEntity(entity: Entity): entity is ToolEntity {
  return entity.apiVersion === NS_API_VERSION && entity.kind === TOOL_KIND;
}
